// plan-05 S06 + plan-10 S03: public heatmap widget interface
// Refactored: exports StageWidget; consensus command activates stage

import type { CommandCtx } from '../../oracle/commands'
import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { createPanel, type HeatmapPanel } from './ui'
import { applyPaintVisuals } from './paint'
import { play } from '../../audio/sfx'
import { load } from '../../core/save'

let activePanel: HeatmapPanel | null = null

function ensurePanel(parent: HTMLElement): HeatmapPanel {
  if (activePanel) {
    // re-parent if needed
    if (!parent.contains(activePanel.el)) {
      parent.appendChild(activePanel.el)
    }
    activePanel.el.style.display = ''
    return activePanel
  }
  activePanel = createPanel()
  if (!parent.contains(activePanel.el)) {
    parent.appendChild(activePanel.el)
  }
  return activePanel
}

// --- StageWidget implementation ---

export const heatmapWidget: StageWidget = {
  id: 'heatmap',
  label: 'HEATMAP.EXE // CONSENSUS GRID',
  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    const p = ensurePanel(host)

    // if args[0] provided, auto-run that word
    const word = ctx.args[0]
    if (word) {
      const input = p.el.querySelector('.vt-heatmap-input') as HTMLInputElement
      if (input) input.value = word
      const runBtn = p.el.querySelectorAll('.vt-heatmap-btn')[0] as HTMLButtonElement
      if (runBtn) runBtn.click()
    }

    return {
      dismiss() {
        p.animCtrl.cancel()
      },
      onSwap() {
        p.animCtrl.cancel()
      },
      getState() {
        return { lastHits: p.lastHits }
      },
    }
  },
}

// --- Oracle command handlers ---

export async function handleConsensus(ctx: CommandCtx, args: string[]): Promise<void> {
  const word = args[0] ?? ''
  if (!word || word.length > 64) {
    const errLine = document.createElement('div')
    errLine.className = 'vt-line-oracle'
    errLine.style.color = 'var(--vt-green-dim)'
    errLine.textContent = '> usage: consensus <word>. 1-64 chars. probably.'
    ctx.history.appendChild(errLine)
    play('command_reject')
    return
  }

  // activate stage with heatmap
  const { getStage } = await import('../stage/stage')
  const stage = getStage()
  if (stage) {
    stage.activate('heatmap', [word])
  }

  // stream acknowledgment into terminal
  const target = document.createElement('div')
  target.className = 'vt-line-oracle'
  target.style.color = 'var(--vt-green-dim)'
  ctx.history.appendChild(target)
  ctx.cursor.attach(target)
  await ctx.streamFn(target, `> consensus: "${word}". panel active.`, { signal: ctx.abortSignal })
  ctx.cursor.detach()
}

export async function handleDecode(ctx: CommandCtx): Promise<void> {
  const { getStage } = await import('../stage/stage')
  const stage = getStage()

  if (!stage?.isActive('heatmap')) {
    const target = document.createElement('div')
    target.className = 'vt-line-oracle'
    target.style.color = 'var(--vt-green-dim)'
    ctx.history.appendChild(target)
    ctx.cursor.attach(target)
    await ctx.streamFn(target, '> decode requires consensus context. type: consensus <word>.', { signal: ctx.abortSignal })
    ctx.cursor.detach()
    return
  }

  const p = activePanel
  if (!p) return

  const lastHits = p.lastHits
  if (!lastHits || lastHits.every(v => v === 0)) {
    const target = document.createElement('div')
    target.className = 'vt-line-oracle'
    target.style.color = 'var(--vt-green-dim)'
    ctx.history.appendChild(target)
    ctx.cursor.attach(target)
    await ctx.streamFn(target, '> no heatmap data. run consensus first.', { signal: ctx.abortSignal })
    ctx.cursor.detach()
    return
  }

  const target = document.createElement('div')
  target.className = 'vt-line-oracle'
  target.style.color = 'var(--vt-green-dim)'
  ctx.history.appendChild(target)
  ctx.cursor.attach(target)
  await ctx.streamFn(target, '> decode engaged. scanning...', { signal: ctx.abortSignal })
  ctx.cursor.detach()

  const btns = p.el.querySelectorAll('.vt-heatmap-btn')
  const decodeBtn = btns[1] as HTMLButtonElement
  if (decodeBtn) decodeBtn.click()

  await new Promise<void>((resolve) => {
    const check = setInterval(() => {
      const decodeEl = p.el.querySelector('.vt-heatmap-decode') as HTMLElement
      if (decodeEl && decodeEl.style.display !== 'none' && decodeEl.textContent && !decodeEl.textContent.startsWith('scanning')) {
        clearInterval(check)
        const resultLine = document.createElement('div')
        resultLine.className = 'vt-line-oracle'
        resultLine.style.color = 'var(--vt-green-dim)'
        resultLine.textContent = `> decode complete: ${decodeEl.textContent}`
        ctx.history.appendChild(resultLine)
        resolve()
      }
    }, 200)
    setTimeout(() => { clearInterval(check); resolve() }, 30000)
  })
}

export async function handlePaint(ctx: CommandCtx, args: string[]): Promise<void> {
  const key = args[0]
  if (!key) {
    const target = document.createElement('div')
    target.className = 'vt-line-oracle'
    target.style.color = 'var(--vt-green-dim)'
    ctx.history.appendChild(target)
    ctx.cursor.attach(target)
    await ctx.streamFn(target, '> usage: paint <key>. recall a saved pattern.', { signal: ctx.abortSignal })
    ctx.cursor.detach()
    return
  }

  const { getStage } = await import('../stage/stage')
  const stage = getStage()
  if (!stage?.isActive('heatmap')) {
    stage?.activate('heatmap')
  }

  const p = activePanel
  if (!p) return

  const s = load()
  const pattern = s.paintedAt[key]

  if (!pattern) {
    const target = document.createElement('div')
    target.className = 'vt-line-oracle'
    target.style.color = 'var(--vt-green-dim)'
    ctx.history.appendChild(target)
    ctx.cursor.attach(target)
    await ctx.streamFn(target, `> pattern "${key}" not found. probably.`, { signal: ctx.abortSignal })
    ctx.cursor.detach()
    play('command_reject')
    return
  }

  p.paintState.paintMode = true
  p.paintState.paintHits = pattern.slice()
  p.paintState.paintedCount = pattern.filter(v => v > 0).length
  p.setLastPaintKey(key)

  const paintBtn = p.el.querySelectorAll('.vt-heatmap-btn')[3] as HTMLButtonElement
  if (paintBtn) paintBtn.textContent = '[ painting ]'
  const paintLabel = p.el.querySelector('.vt-heatmap-paint-label') as HTMLElement
  if (paintLabel) {
    paintLabel.style.display = 'block'
    paintLabel.textContent = `${p.paintState.paintedCount} hex${p.paintState.paintedCount !== 1 ? 'es' : ''} painted -- pattern: ${key}`
  }
  const saveBtn = p.el.querySelectorAll('.vt-heatmap-btn')[4] as HTMLElement
  if (saveBtn) saveBtn.style.display = ''

  applyPaintVisuals(p.grid, p.paintState)

  const target = document.createElement('div')
  target.className = 'vt-line-oracle'
  target.style.color = 'var(--vt-green-dim)'
  ctx.history.appendChild(target)
  ctx.cursor.attach(target)
  await ctx.streamFn(target, `> pattern "${key}" loaded. ${p.paintState.paintedCount} hexes.`, { signal: ctx.abortSignal })
  ctx.cursor.detach()
  play('quorum_chime')
}
